import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ServiceDeal } from 'services/service.deal';
import ServiceEventTracking from 'services/service.event-tracking';
import {
  ConsultancyFormFieldsValidationSchema,
  FORM_FIELDS_DEFAULT_VALUES
} from './Form.constants';
import { ConsultancyFormFields, UseConsultancyForm } from './Form.types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const useConsultancyForm: UseConsultancyForm = () => {
  const { t } = useI18n('consultancy');
  const { locale } = useRouter();
  const { addErrorToast, addSuccessToast } = useToasts();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formMethods = useForm<ConsultancyFormFields>({
    defaultValues: FORM_FIELDS_DEFAULT_VALUES,
    resolver: zodResolver(ConsultancyFormFieldsValidationSchema)
  });

  const isTermsAccepted = !!formMethods.watch('terms');

  const onSubmit = useCallback(
    async (data: ConsultancyFormFields) => {
      try {
        if (!executeRecaptcha) {
          console.error('Recaptcha not available');
          return;
        }

        const recaptchaToken = await executeRecaptcha('consultancy_form');
        await ServiceDeal.create({
          ...data,
          howDoYouKnowUs: data.howDoYouKnowUs || 'OTHER',
          source: 'Manfred Web - Consulting',
          consultingOptions: [],
          recaptchaToken
        });

        formMethods.reset(FORM_FIELDS_DEFAULT_VALUES);
        addSuccessToast(t('consultancy.form.success'));
      } catch (ex) {
        console.error(
          `There was an error while submitting the Consultancy form. Form data: ${data}. Exception: ${ex}`
        );

        addErrorToast(t('consultancy.form.error'));
      }
    },
    [addErrorToast, addSuccessToast, formMethods, executeRecaptcha, t]
  );

  const onFormFieldFocus = useCallback(() => {
    ServiceEventTracking.track({
      type: 'Landing - Form Focused',
      properties: {
        objectName: 'contacto-empresas',
        url: window.location.href,
        urlParams: new URLSearchParams(window.location.search).toString()
      }
    });
  }, []);

  return {
    t,
    locale,
    formMethods,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  };
};
